/**
 * Autofix Backend Client
 *
 * HTTP client for GraphQL queries to the Sailfish production backend.
 * Reads JWT from a file (written by `npx @sailfish-ai/autofix login` or the
 * desktop app) so the MCP server can authenticate.
 *
 * The JWT file is re-read periodically (30s cache) to pick up token refreshes.
 */

import * as fs from "fs";
import { CREDENTIALS_MODE } from "../constants.js";

interface JwtFileContents {
  jwt: string;
  graphqlUrl: string;
  updatedAt: number;
  source?: string;
}

interface GraphQLResponse<T> {
  data?: T;
  errors?: Array<{ message: string; path?: string[] }>;
}

const AUTH_CACHE_TTL_MS = 30_000;
const DEFAULT_TIMEOUT_MS = 30_000;
const MAX_RETRIES = 3;
/** Proactively refresh when token expires within this window */
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

const REFRESH_TOKEN_MUTATION = `mutation { refreshToken }`;

export class AutofixBackendClient {
  private jwtFilePath: string;
  private cachedAuth: (JwtFileContents & { readAt: number }) | null = null;
  private refreshInFlight: Promise<string> | null = null;

  constructor(jwtFilePath: string) {
    this.jwtFilePath = jwtFilePath;
  }

  private getAuth(): JwtFileContents {
    const now = Date.now();
    if (this.cachedAuth && now - this.cachedAuth.readAt < AUTH_CACHE_TTL_MS) {
      return this.cachedAuth;
    }

    if (!fs.existsSync(this.jwtFilePath)) {
      throw new Error(
        "Not authenticated. Run `npx @sailfish-ai/autofix login` or sign in to the Sailfish desktop app."
      );
    }

    try {
      const raw = JSON.parse(fs.readFileSync(this.jwtFilePath, "utf-8"));
      if (!raw.jwt || !raw.graphqlUrl) {
        throw new Error("Invalid credentials file: missing jwt or graphqlUrl");
      }
      this.cachedAuth = { ...raw, readAt: now };
      return this.cachedAuth!;
    } catch (err: any) {
      if (err.message?.includes("Not authenticated") || err.message?.includes("Invalid credentials")) {
        throw err;
      }
      throw new Error(`Failed to read credentials: ${err.message}`);
    }
  }

  /**
   * Check if a JWT is near expiry (within REFRESH_BUFFER_MS).
   */
  private isNearExpiry(jwt: string): boolean {
    try {
      const payload = JSON.parse(
        Buffer.from(jwt.split(".")[1], "base64").toString()
      );
      const exp = payload.exp * 1000;
      return Date.now() > exp - REFRESH_BUFFER_MS;
    } catch {
      return false;
    }
  }

  /**
   * Call the refreshToken mutation using the current JWT.
   * Returns the new JWT on success, or null if refresh fails.
   */
  private async refreshToken(currentJwt: string, graphqlUrl: string): Promise<string | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

      try {
        const response = await fetch(graphqlUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentJwt}`,
            "X-Sailfish-MCP": "1",
          },
          body: JSON.stringify({ query: REFRESH_TOKEN_MUTATION }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) return null;

        const result = (await response.json()) as GraphQLResponse<{ refreshToken: string | null }>;
        return result.data?.refreshToken ?? null;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch {
      return null;
    }
  }

  /**
   * Persist a refreshed JWT to the credentials file and update cache.
   */
  private persistRefreshedToken(newJwt: string, auth: JwtFileContents): void {
    const updated: JwtFileContents = {
      ...auth,
      jwt: newJwt,
      updatedAt: Date.now(),
    };
    try {
      fs.writeFileSync(this.jwtFilePath, JSON.stringify(updated, null, 2), {
        mode: CREDENTIALS_MODE,
      });
      this.cachedAuth = { ...updated, readAt: Date.now() };
    } catch (err: any) {
      console.error(`[AutofixBackendClient] Failed to persist refreshed token: ${err.message}`);
    }
  }

  /**
   * Ensure the JWT is fresh. Proactively refreshes if near expiry.
   * Deduplicates concurrent refresh attempts.
   */
  private async ensureFreshAuth(): Promise<JwtFileContents> {
    const auth = this.getAuth();

    if (!this.isNearExpiry(auth.jwt)) {
      return auth;
    }

    // Deduplicate concurrent refresh calls
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.refreshToken(auth.jwt, auth.graphqlUrl).then((newJwt) => {
        this.refreshInFlight = null;
        if (newJwt) {
          this.persistRefreshedToken(newJwt, auth);
          return newJwt;
        }
        return auth.jwt;
      }).catch(() => {
        this.refreshInFlight = null;
        return auth.jwt;
      });
    }

    const jwt = await this.refreshInFlight;
    return { ...auth, jwt };
  }

  async query<T>(query: string, variables?: Record<string, any>): Promise<T> {
    let auth = await this.ensureFreshAuth();
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

        try {
          const response = await fetch(auth.graphqlUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${auth.jwt}`,
              "X-Sailfish-MCP": "1",
            },
            body: JSON.stringify({ query, variables }),
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (response.status === 401 || response.status === 403) {
            // Attempt a reactive refresh before giving up
            this.cachedAuth = null;
            const newJwt = await this.refreshToken(auth.jwt, auth.graphqlUrl);
            if (newJwt) {
              this.persistRefreshedToken(newJwt, auth);
              auth = { ...auth, jwt: newJwt };
              continue; // Retry with the refreshed token
            }
            throw new Error(
              `Authentication failed (${response.status}). Run \`npx @sailfish-ai/autofix login\` to re-authenticate.`
            );
          }

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const result = (await response.json()) as GraphQLResponse<T>;

          if (result.errors && result.errors.length > 0) {
            throw new Error(result.errors.map((e) => e.message).join(", "));
          }

          if (!result.data) {
            throw new Error("No data returned from GraphQL query");
          }

          return result.data;
        } finally {
          clearTimeout(timeoutId);
        }
      } catch (error: any) {
        lastError = error;

        if (error.name === "AbortError") {
          throw new Error(`Request timeout after ${DEFAULT_TIMEOUT_MS}ms`);
        }

        if (error.message?.includes("Authentication failed") || error.message?.includes("Not authenticated")) {
          throw error;
        }

        if (
          error.cause?.code === "ECONNREFUSED" ||
          error.message?.includes("ECONNREFUSED") ||
          error.message?.includes("fetch failed") ||
          error.message?.includes("network")
        ) {
          if (attempt < MAX_RETRIES) {
            const delay = Math.pow(2, attempt - 1) * 1000;
            console.error(
              `[AutofixBackendClient] Network error, retrying in ${delay}ms (${attempt}/${MAX_RETRIES})`
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }
        }

        throw error;
      }
    }

    throw lastError || new Error("GraphQL query failed after retries");
  }

  isAvailable(): boolean {
    try {
      this.getAuth();
      return true;
    } catch {
      return false;
    }
  }
}
